import { AngularFirestore } from "@angular/fire/firestore"
import { TestCase } from "@tr/models"

export class TestCaseUtils {
  private static deviceMultiplier = 1
  public static emptyTestCase(testCase: Partial<TestCase> = {}): TestCase {
    return {
      isLoginRequired: true,
      jiraID: "",
      lastTested: "",
      notes: "",
      numberSteps: 0,
      selectedDevices: [],
      selectedEmulators: [],
      selectedFeatures: [],
      selectedPlatforms: [],
      selectedTags: [],
      startInstructions: "",
      status: "New",
      testID: "",
      testSteps: [],
      testType: "",
      title: "",
      parents: [],
      ...testCase,
    } as TestCase
  }
  public static async deleteTestCase(afs: AngularFirestore, company: string, project: string, testCase: any, testCaseId: string): Promise<void> {
    await afs
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection("Test Cases")
      .doc(testCaseId)
      .ref
      .delete()

    // updating scheduledTestRuns and TestRuns associated with the test case
    await this.getRunCompletionVariables(afs)
    await this.updateAssociatedScheduledTestRuns(afs, company, project, testCase, testCaseId)
    await this.updateAssociatedTestRuns(afs, company, project, testCase, testCaseId)
  }

  public static async updateAssociatedScheduledTestRuns(afs: AngularFirestore, company: string, project: string, testCase: any, testCaseId: string): Promise<void> {
    const scheduledTestRunRef = await afs
      .collection("ScheduledTestRuns")
      .ref
      .where("company", "==", company)
      .where("project", "==", project)
      .where("selectedTests", "array-contains", testCaseId)
      .get()

    await this.updateTestRun(testCase, testCaseId, scheduledTestRunRef)
  }

  public static async updateAssociatedTestRuns(afs: AngularFirestore, company: string, project: string, testCase: any, testCaseId: string): Promise<void> {
    const testRunRef = await afs
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection("Test Runs")
      .ref
      .where("company", "==", company)
      .where("project", "==", project)
      .where("selectedTests", "array-contains", testCaseId)
      .get()

    await this.updateTestRun(testCase, testCaseId, testRunRef)
  }

  public static async updateTestRun(testCase: any, testCaseId: string, testRunRef: firebase.firestore.QuerySnapshot): Promise<void> {
    const deleteAndUpdateActions = testRunRef.docs.map(testRunSnapshot => {
      const testRunData = testRunSnapshot.data()
      let selectedTests = testRunData.selectedTests
      if (selectedTests.length === 1) {
        testRunSnapshot.ref.delete()
      } else {
        const totalSteps = testRunData.totalSteps - testCase.numberSteps
        const totalTests = testRunData.totalTests - 1
        const totalCredits = this.calculateTotalCredits(testRunData, totalSteps)
        selectedTests = selectedTests.filter(testId => testId !== testCaseId)
        testRunSnapshot.ref.update({totalSteps, totalTests, totalCredits, selectedTests})
      }
    })
    await Promise.all(deleteAndUpdateActions)
  }

  public static calculateTotalCredits(testRun: any, totalSteps: number): number {
    const runFactor = testRun.runPace
    const totalEnvironments = testRun.selectedEnvironments.length
    let platformTotal = 0
    const selectedRunPlatforms = testRun.selectedRunPlatforms
    if (testRun.isMobileRun) {
      const platformCounts = this.iosAndroidCounts(selectedRunPlatforms)
      const platformIos = platformCounts.iosCount
      const platformAndroid = platformCounts.androidCount

      const deviceCounts = this.iosAndroidCounts(testRun.selectedOsVersionsDevices)
      const deviceIos = deviceCounts.iosCount
      const deviceAndroid = deviceCounts.androidCount

      const emulatorCounts = this.iosAndroidCounts(testRun.selectedOsVersionsEmulators)
      const emulatorIos = emulatorCounts.iosCount
      const emulatorAndroid = emulatorCounts.androidCount

      const iosTotal = deviceIos * this.deviceMultiplier + emulatorIos
      const androidTotal = deviceAndroid * this.deviceMultiplier + emulatorAndroid

      platformTotal = iosTotal * platformIos + androidTotal * platformAndroid

    } else {
      const browserPlatforms = testRun.selectedOsVersionsBrowsers.map(browser => browser.platform)
      const runPlatforms = selectedRunPlatforms.filter(platform => !browserPlatforms.includes(platform))
      platformTotal = browserPlatforms.length + runPlatforms.length
    }
    return totalSteps * runFactor * totalEnvironments * platformTotal
  }

  private static iosAndroidCounts(platforms: any[]): {iosCount: number, androidCount: number} {
    let iosCount = 0
    let androidCount = 0
    platforms.forEach(platform => {
      if (platform === "iOS") {
        iosCount ++
      }
      if (platform === "Android") {
        androidCount ++
      }
    })
    return {iosCount, androidCount}
  }

  private static async getRunCompletionVariables(afs: AngularFirestore): Promise<void> {
    const runCompletionVariablesSnapshot = await afs
      .collection("globalVariables")
      .doc("runCompletionVariables")
      .ref.get()

    const runCompletionVariables = runCompletionVariablesSnapshot.data()
    if (runCompletionVariables) {
      this.deviceMultiplier = runCompletionVariables.deviceMultiplier
    }
  }
}
